import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, Upload, Download, FileText, Lock } from "lucide-react";

const CATEGORIES = ["Apparel", "Electronics", "Gadgets", "Home & Garden", "Furniture", "Kitchen & Dining", "Beauty", "Health", "Personal Care", "Sports", "Outdoors", "Fitness", "Toys", "Baby Products", "Kids & Nursery", "Pet Supplies", "Food & Beverage", "Grocery", "Jewelry", "Watches", "Accessories", "Bags & Luggage", "Footwear", "Books", "Education", "Stationery & Office", "Arts & Crafts", "Music & Instruments", "Automotive", "Tools & Hardware", "Industrial & Scientific", "Software & Digital", "Gaming", "Mobile & Computers", "Cameras & Photography", "Travel", "Wedding & Events", "Garden & Outdoor", "Cleaning & Household", "Wellness & Supplements", "Other"];
const LANGUAGES = ["English", "Spanish", "French", "German", "Italian", "Portuguese", "Dutch", "Japanese"];
const MAX_BATCH = 10;

type Row = { product_name: string; description?: string; category?: string; language?: string };
type ResultRow = Row & { seo_title?: string; seo_description?: string; keywords?: string[]; tags?: string[]; seo_score?: number; error?: string };

function parseCSV(text: string): Row[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ""));
  const rows: Row[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].match(/(?:"([^"]*)"|([^,]+))/g) ?? [];
    const get = (name: string) => {
      const idx = headers.indexOf(name);
      if (idx === -1 || !cols[idx]) return "";
      return cols[idx].replace(/^,?"?|"?$/g, "").trim();
    };
    const product_name = get("product_name") || get("name") || get("product");
    if (!product_name) continue;
    rows.push({
      product_name,
      description: get("description"),
      category: get("category"),
      language: get("language"),
    });
  }
  return rows;
}

function csvEscape(v: any): string {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCSV(results: ResultRow[]) {
  const headers = ["product_name", "category", "language", "seo_title", "seo_description", "keywords", "tags", "seo_score", "error"];
  const lines = [headers.join(",")];
  for (const r of results) {
    lines.push([
      r.product_name, r.category, r.language, r.seo_title, r.seo_description,
      r.keywords?.join("|"), r.tags?.join("|"), r.seo_score, r.error,
    ].map(csvEscape).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `seo-bulk-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Bulk() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState("");
  const [csvRows, setCsvRows] = useState<Row[]>([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [sharedCategory, setSharedCategory] = useState("");
  const [sharedLanguage, setSharedLanguage] = useState("English");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("plan").eq("id", user.id).maybeSingle().then(({ data }) => {
      setPlan(data?.plan ?? "free");
    });
  }, [user]);

  if (plan && plan !== "pro") {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="bg-gradient-card border-border/50">
          <CardHeader>
            <div className="h-12 w-12 rounded-lg bg-gradient-primary flex items-center justify-center mb-2">
              <Lock className="h-6 w-6 text-primary-foreground" />
            </div>
            <CardTitle>Bulk Generation is a Pro feature</CardTitle>
            <CardDescription>
              Upgrade to Pro to generate SEO listings for up to {MAX_BATCH} products at once and export them as a CSV.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="text-sm space-y-1.5 text-muted-foreground">
              <li>• Up to {MAX_BATCH} products per batch</li>
              <li>• CSV upload or paste-list input</li>
              <li>• Export results as CSV</li>
              <li>• Auto-saved to your history</li>
            </ul>
            <Button onClick={() => navigate("/settings")} className="bg-gradient-primary shadow-glow">
              Upgrade to Pro
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length === 0) {
      toast.error("Could not parse any rows. Check that your CSV has a 'product_name' column.");
      return;
    }
    setCsvFileName(file.name);
    setCsvRows(rows.slice(0, MAX_BATCH));
    if (rows.length > MAX_BATCH) {
      toast.warning(`Only the first ${MAX_BATCH} rows will be processed.`);
    }
  };

  const buildRowsFromPaste = (): Row[] => {
    return pasteText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean)
      .slice(0, MAX_BATCH)
      .map((name) => ({
        product_name: name,
        category: sharedCategory || undefined,
        language: sharedLanguage,
      }));
  };

  const submit = async (rows: Row[]) => {
    if (rows.length === 0) {
      toast.error("Add at least one product.");
      return;
    }
    const enriched = rows.map((r) => ({
      ...r,
      category: r.category || sharedCategory || undefined,
      language: r.language || sharedLanguage,
    }));
    setLoading(true);
    setResults(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-seo-bulk", { body: { rows: enriched } });
      if (error) throw error;
      if (data?.error === "pro_required") {
        toast.error(data.message ?? "Pro required");
        return;
      }
      if (data?.error === "limit_reached") {
        toast.error(data.message ?? "Limit reached");
        return;
      }
      if (data?.error) throw new Error(data.error);
      setResults(data.results);
      toast.success(`Generated ${data.success_count} of ${data.processed} listings`);
      if (data.skipped_due_to_limit > 0) {
        toast.warning(`${data.skipped_due_to_limit} row(s) skipped - monthly limit reached.`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Bulk generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            Bulk Generation
            <Badge className="bg-gradient-primary">Pro</Badge>
          </h1>
          <p className="text-muted-foreground">Generate SEO listings for up to {MAX_BATCH} products at once.</p>
        </div>
      </div>

      <Card className="bg-gradient-card border-border/50">
        <CardHeader>
          <CardTitle>Input products</CardTitle>
          <CardDescription>Paste a list or upload a CSV. Each generation counts toward your monthly limit.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="paste">
            <TabsList>
              <TabsTrigger value="paste"><FileText className="h-4 w-4 mr-1" />Paste list</TabsTrigger>
              <TabsTrigger value="csv"><Upload className="h-4 w-4 mr-1" />CSV upload</TabsTrigger>
            </TabsList>

            <TabsContent value="paste" className="space-y-4 mt-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Shared category (optional)</Label>
                  <Select value={sharedCategory} onValueChange={setSharedCategory}>
                    <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Language</Label>
                  <Select value={sharedLanguage} onValueChange={setSharedLanguage}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Product names (one per line, max {MAX_BATCH})</Label>
                <Textarea
                  rows={8}
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={"Wireless Noise-Cancelling Headphones\nOrganic Cotton T-Shirt\nStainless Steel Water Bottle"}
                />
                <p className="text-xs text-muted-foreground">
                  {pasteText.split(/\r?\n/).filter((l) => l.trim()).length} product(s) detected
                </p>
              </div>
              <Button
                onClick={() => submit(buildRowsFromPaste())}
                disabled={loading || !pasteText.trim()}
                className="bg-gradient-primary shadow-glow"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate batch</>}
              </Button>
            </TabsContent>

            <TabsContent value="csv" className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label>CSV file</Label>
                <Input type="file" accept=".csv,text/csv" onChange={onFile} />
                <p className="text-xs text-muted-foreground">
                  Required column: <code>product_name</code>. Optional: <code>description</code>, <code>category</code>, <code>language</code>.
                </p>
              </div>
              {csvRows.length > 0 && (
                <div className="text-sm">
                  <p className="font-medium">{csvFileName}</p>
                  <p className="text-muted-foreground">{csvRows.length} product(s) ready</p>
                </div>
              )}
              <Button
                onClick={() => submit(csvRows)}
                disabled={loading || csvRows.length === 0}
                className="bg-gradient-primary shadow-glow"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate batch</>}
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {loading && (
        <Card className="bg-gradient-card border-border/50">
          <CardContent className="py-10 text-center space-y-3">
            <div className="h-14 w-14 rounded-full bg-gradient-primary mx-auto flex items-center justify-center shadow-glow animate-pulse">
              <Sparkles className="h-7 w-7 text-primary-foreground" />
            </div>
            <p className="font-semibold">Processing your batch...</p>
            <p className="text-sm text-muted-foreground">This can take a minute. Please don't close this tab.</p>
          </CardContent>
        </Card>
      )}

      {results && results.length > 0 && (
        <Card className="bg-gradient-card border-border/50">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Results</CardTitle>
              <CardDescription>{results.filter((r) => !r.error).length} successful · {results.filter((r) => r.error).length} failed</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => downloadCSV(results)}>
                <Download className="h-4 w-4 mr-2" />Download CSV
              </Button>
              <Button variant="outline" asChild>
                <Link to="/history">View in History</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {results.map((r, i) => (
                <div key={i} className="border border-border/50 rounded-lg p-3 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium">{r.product_name}</div>
                    {r.error ? (
                      <Badge variant="destructive">{r.error}</Badge>
                    ) : (
                      <Badge variant="secondary">Score {r.seo_score}</Badge>
                    )}
                  </div>
                  {!r.error && (
                    <div className="mt-2 space-y-1 text-muted-foreground">
                      <div><span className="text-foreground font-medium">Title:</span> {r.seo_title}</div>
                      <div className="line-clamp-2"><span className="text-foreground font-medium">Description:</span> {r.seo_description}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
